/**
 * ImpersonationBanner
 *
 * Persistent indicator shown while an admin is impersonating a member.
 * Reads the impersonation metadata stored in localStorage at impersonation
 * start. "Exit" restores the admin's original token (saved as admin_token)
 * without requiring a fresh login.
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ShieldOff, UserCheck } from 'lucide-react';

const ImpersonationBanner = () => {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('impersonation');
      const adminToken = localStorage.getItem('admin_token');
      if (raw && adminToken) setInfo(JSON.parse(raw));
    } catch {
      setInfo(null);
    }
  }, []);

  const exitImpersonation = () => {
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) {
      localStorage.setItem('jwt_token', adminToken);
    }
    localStorage.removeItem('admin_token');
    localStorage.removeItem('impersonation');
    window.location.href = '/admin/members';
  };

  if (!info) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-black shadow-md"
      data-testid="impersonation-banner"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 px-4 py-2 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <UserCheck className="w-4 h-4 shrink-0" />
          <span className="font-medium truncate">
            Viewing as {info.memberName} — Admin: {info.adminName}
          </span>
        </div>
        <Button
          size="sm"
          variant="secondary"
          onClick={exitImpersonation}
          className="bg-black text-white hover:bg-black/80 shrink-0"
          data-testid="exit-impersonation-btn"
        >
          <ShieldOff className="w-4 h-4 mr-1" /> Exit Impersonation
        </Button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
